import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Image as ImageIcon, X, Loader2, FileText, ExternalLink } from "lucide-react";

/**
 * Shows a receipt or document (image or PDF) linked to a refuel/document/bill.
 * Props: path (string). Fetches as blob using axios (so Authorization header is respected).
 */
export default function ReceiptViewer({ path, size = "sm" }) {
  const [url, setUrl] = useState(null);
  const [contentType, setContentType] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    if (!path) { setLoading(false); return; }
    (async () => {
      try {
        setLoading(true); setErr(false);
        const res = await api.get(`/files/${path}`, { responseType: "blob" });
        objectUrl = URL.createObjectURL(res.data);
        if (!cancelled) {
          setUrl(objectUrl);
          setContentType(res.data.type || res.headers?.["content-type"] || "");
        }
      } catch (e) {
        if (!cancelled) setErr(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  if (!path) return null;
  if (loading) return <Loader2 size={14} className="animate-spin text-[#737373]" />;
  if (err) return <span className="text-[#EF4444] text-xs">file error</span>;

  const isPdf = contentType.includes("pdf") || path.toLowerCase().endsWith(".pdf");
  const dims = size === "lg" ? "w-32 h-32" : "w-10 h-10";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`${dims} border border-[#262626] bg-[#0A0A0A] overflow-hidden hover:border-[#FF5A1F] transition-colors flex items-center justify-center`}
        data-testid="receipt-thumb"
        title={isPdf ? "View PDF" : "View image"}
      >
        {isPdf ? (
          <div className="flex flex-col items-center gap-0.5 text-[#FF5A1F]">
            <FileText size={size === "lg" ? 32 : 18} strokeWidth={2} />
            <span className="text-[8px] font-mono font-bold uppercase">PDF</span>
          </div>
        ) : (
          <img src={url} alt="document" className="w-full h-full object-cover" />
        )}
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur p-6"
          onClick={() => setOpen(false)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-[#FF5A1F]"
            onClick={() => setOpen(false)}
            data-testid="receipt-close"
          >
            <X size={28} />
          </button>
          {isPdf ? (
            <div className="w-full h-full max-w-5xl flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-3 pb-3">
                <FileText size={20} className="text-[#FF5A1F]" />
                <span className="font-mono text-sm text-white truncate">{path.split("/").pop()}</span>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#A3A3A3] hover:text-[#FF5A1F] transition-colors"
                >
                  Open in new tab <ExternalLink size={12} />
                </a>
              </div>
              <iframe
                src={url}
                title="document PDF"
                className="flex-1 w-full bg-white border border-[#262626]"
                data-testid="receipt-fullsize-pdf"
              />
            </div>
          ) : (
            <img
              src={url}
              alt="document full"
              className="max-w-full max-h-full border border-[#262626]"
              data-testid="receipt-fullsize"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </>
  );
}

export function ReceiptEmpty() {
  return (
    <span className="inline-flex items-center gap-1 text-[#404040] text-xs">
      <ImageIcon size={12} /> none
    </span>
  );
}
